import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetEvent } from "../../api/queries/useGetEvent";
import { useMakePayment } from "../../api/mutations/useMakePayment";
import { useBookEvent } from "../../api/mutations/useBookEvent";
import { useQueryClient } from "@tanstack/react-query";
import { useGetBilling } from "../../api/queries/useGetBilling";
import { useAppContext } from "../../Context/AppContext";
import {
  logPaymentError,
  getPaymentErrorSource,
  getPaymentErrorMessage,
} from "../../utils/paymentErrorLogger";

const EventSuccessModal = lazy(() => import("./EventSuccessModal"));

const EVENT_DATA_KEY = "event-data";
const BACK_ROUTE = "/event-booking";
const MEMBERSHIP_BUSINESS = "Business Membership";
const MEMBERSHIP_EXECUTIVE = "Executive Membership";
const MEMBERSHIP_YOUTH = "Youth Membership";

const STRIPE_ELEMENT_STYLE = {
  base: {
    fontSize: "16px",
    color: "#000",
    "::placeholder": { color: "#999" },
  },
  invalid: { color: "red" },
} as const;

interface PriceSummaryProps {
  data: { price?: number; earlyBirdPrice?: number } | undefined;
  isEarlyBird: boolean;
  finalPrice: string;
  membershipName: string | undefined;
}

const PriceSummary = memo(function PriceSummary({
  data,
  isEarlyBird,
  finalPrice,
  membershipName,
}: PriceSummaryProps) {
  return (
    <>
      <div className="d-flex justify-content-between">
        <span>Price</span>
        <span className="fw-bold">
          £{data?.price != null ? data.price.toFixed(2) : "0.00"}
        </span>
      </div>
      {isEarlyBird && (
        <div className="d-flex justify-content-between">
          <span>Early Bird Discount</span>
          <span className="fw-bold text-success">
            -£
            {data?.earlyBirdPrice != null
              ? data.earlyBirdPrice.toFixed(2)
              : "0.00"}
          </span>
        </div>
      )}
      {membershipName !== MEMBERSHIP_YOUTH && (
        <div className="d-flex justify-content-between">
          <span>
            {membershipName === MEMBERSHIP_BUSINESS
              ? "Business Member Discount"
              : "Executive Member Discount"}
          </span>
          <span className="fw-bold text-success text-nowrap">
            -£{finalPrice}
          </span>
        </div>
      )}
      <div className="d-flex justify-content-between">
        <span>Total</span>
        <span className="fw-bold">£{finalPrice}</span>
      </div>
    </>
  );
});
PriceSummary.displayName = "PriceSummary";

interface PaymentMethodChoiceProps {
  paymentMethods: { last4?: string; brand?: string }[] | undefined;
  useSavedMethod: boolean;
  onUseSaved: () => void;
  onUseNew: () => void;
}

const PaymentMethodChoice = memo(function PaymentMethodChoice({
  paymentMethods,
  useSavedMethod,
  onUseSaved,
  onUseNew,
}: PaymentMethodChoiceProps) {
  if (!paymentMethods?.length) return null;
  return (
    <div className="mb-3">
      <label className="form-label">Choose Payment Method</label>
      <div className="form-check mb-2">
        <input
          type="radio"
          className="form-check-input"
          id="savedCard"
          checked={useSavedMethod}
          onChange={onUseSaved}
          aria-label="Use saved card"
        />
        <label className="form-check-label" htmlFor="savedCard">
          Use saved card (**** **** **** {paymentMethods[0].last4},{" "}
          {paymentMethods[0].brand})
        </label>
      </div>
      <div className="form-check">
        <input
          type="radio"
          className="form-check-input"
          id="newCard"
          checked={!useSavedMethod}
          onChange={onUseNew}
          aria-label="Use a different card"
        />
        <label className="form-check-label" htmlFor="newCard">
          Use a different card
        </label>
      </div>
    </div>
  );
});
PaymentMethodChoice.displayName = "PaymentMethodChoice";

function ConfirmBooking() {
  const { eventId } = useParams();
  const { objectId } = useAppContext();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [billingAddress, setBillingAddress] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [useSavedMethod, setUseSavedMethod] = useState(false);
  const submitLockRef = useRef(false);
  const navigate = useNavigate();
  const { mutate } = useMakePayment();
  const queryClient = useQueryClient();
  const { user } = useAppContext();

  const onBookSuccess = useCallback(
    (data: unknown) => {
      queryClient.invalidateQueries({ queryKey: ["booked-events", data] });
      localStorage.removeItem(EVENT_DATA_KEY);
    },
    [queryClient],
  );
  const { mutate: bookEvent } = useBookEvent({ onSuccess: onBookSuccess });
  const { data: paymentMethods } = useGetBilling({ userId: objectId || "" });
  const { data } = useGetEvent({ eventId: eventId || "" });

  const isBusinessOrExecutive = useMemo(
    () =>
      user?.user?.membershipName === MEMBERSHIP_BUSINESS ||
      user?.user?.membershipName === MEMBERSHIP_EXECUTIVE,
    [user?.user?.membershipName],
  );

  const totalPrice = useMemo(() => {
    if (
      data?.price &&
      data?.earlyBirdPrice &&
      data?.earlyBirdDateEnd &&
      new Date(data.earlyBirdDateEnd) > new Date()
    ) {
      return (data.price - data.earlyBirdPrice).toFixed(2);
    }
    return data?.price ? data.price.toFixed(2) : "0.00";
  }, [data?.price, data?.earlyBirdPrice, data?.earlyBirdDateEnd]);

  const finalPrice = useMemo(
    () =>
      isBusinessOrExecutive
        ? (parseFloat(totalPrice) / 2).toFixed(2)
        : totalPrice,
    [isBusinessOrExecutive, totalPrice],
  );

  const isEarlyBird = useMemo(
    () =>
      !!data?.earlyBirdDateEnd && new Date(data.earlyBirdDateEnd) > new Date(),
    [data?.earlyBirdDateEnd],
  );

  const eventData = useMemo(() => {
    const stored = localStorage.getItem(EVENT_DATA_KEY);
    return stored ? JSON.parse(stored) : null;
  }, []);

  useEffect(() => {
    if ((paymentMethods?.length ?? 0) > 0) {
      setUseSavedMethod(true);
    }
  }, [paymentMethods?.length]);

  // Handle return from 3DS redirect: complete booking when user lands back with success
  useEffect(() => {
    if (!stripe || !eventId || !objectId) return;

    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get("payment_intent_client_secret");
    const redirectStatus = params.get("redirect_status");

    if (clientSecret && redirectStatus === "succeeded") {
      stripe
        .retrievePaymentIntent(clientSecret)
        .then(({ paymentIntent }) => {
          if (paymentIntent?.status === "succeeded") {
            const stored = localStorage.getItem(EVENT_DATA_KEY);
            const data = stored ? JSON.parse(stored) : null;
            const sectors = data?.sectorsOfInterest;
            bookEvent({
              stageOfBusiness: data?.stageOfBusiness ?? "",
              eventObjectives: data?.objectives ?? "",
              sectorsOfInterest: Array.isArray(sectors) ? sectors.join(", ") : "",
              consentToShareProfile: data?.consentToShare ? 1 : 0,
              eventId: parseInt(eventId, 10),
              userId: objectId,
            });
            setShowSuccessModal(true);
            window.history.replaceState({}, "", window.location.pathname);
          }
        })
        .catch(() => {});
    }
  }, [stripe, eventId, objectId, bookEvent]);

  const handleBack = useCallback(() => {
    navigate(BACK_ROUTE);
  }, [navigate]);

  const handleHideSuccess = useCallback(() => setShowSuccessModal(false), []);

  const stripeElementOptions = useMemo(
    () => ({ style: STRIPE_ELEMENT_STYLE }),
    [],
  );

  const handleUseSavedMethod = useCallback(() => {
    setUseSavedMethod(true);
  }, []);

  const handleUseNewMethod = useCallback(() => {
    setUseSavedMethod(false);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (submitLockRef.current) return;
      if (!stripe || !elements) {
        const msg = "Stripe.js has not loaded yet.";
        logPaymentError({
          flow: "event-booking",
          errorSource: "frontend",
          message: msg,
        });
        setError(msg);
        return;
      }

      submitLockRef.current = true;
      setLoading(true);
      setError("");

      try {
        const cardNumberElement = elements?.getElement(CardNumberElement);

        if (!stripe || !elements) {
          const msg = "Stripe has not loaded.";
          logPaymentError({
            flow: "event-booking",
            errorSource: "frontend",
            message: msg,
          });
          setError(msg);
          setLoading(false);
          submitLockRef.current = false;
          return;
        }

        let newPaymentMethod;
        if (!useSavedMethod && cardNumberElement) {
          const { paymentMethod, error: pmError } =
            await stripe.createPaymentMethod({
              type: "card",
              card: cardNumberElement,
            });
          if (pmError || !paymentMethod) {
            const msg = pmError?.message || "Card creation failed.";
            logPaymentError({
              flow: "event-booking",
              errorSource: "stripe",
              message: msg,
              error: pmError,
            });
            setError(msg);
            setLoading(false);
            submitLockRef.current = false;
            return;
          }
          newPaymentMethod = paymentMethod;
        }

        const selectedPaymentMethodId =
          useSavedMethod && paymentMethods?.[0]?.paymentMethodId
            ? paymentMethods[0].paymentMethodId
            : newPaymentMethod?.id;

        if (!selectedPaymentMethodId) {
          const msg = "No valid payment method found.";
          logPaymentError({
            flow: "event-booking",
            errorSource: "frontend",
            message: msg,
          });
          setError(msg);
          setLoading(false);
          submitLockRef.current = false;
          return;
        }

        mutate(
          {
            paymentMethodTypes: ["card"],
            customerId: "",
            paymentMethodId: selectedPaymentMethodId,
            userId: objectId || "",
            name: `${user?.user?.firstName || ""} ${
              user?.user?.lastName || ""
            }`.trim(),
            email: user?.user?.email || "",
            productId: data?.productId,
            discountCode: "",
            IsEventEarlyBird: isEarlyBird,
            isEvent: true,
          },
          {
            onSuccess: async (response) => {
              const clientSecret = response.clientSecret;
              if (!clientSecret && !response.paymentAlreadySucceeded) {
                const msg = "No client secret returned.";
                logPaymentError({
                  flow: "event-booking",
                  errorSource: "backend",
                  message: msg,
                  responseData: response,
                });
                setError(msg);
                setLoading(false);
                submitLockRef.current = false;
                return;
              }

              // Payment already succeeded on server (e.g. saved card) — skip confirmCardPayment to avoid payment_intent_unexpected_state
              if (response.paymentAlreadySucceeded) {
                const sectors = eventData?.sectorsOfInterest;
                bookEvent({
                  stageOfBusiness: eventData?.stageOfBusiness ?? "",
                  eventObjectives: eventData?.objectives ?? "",
                  sectorsOfInterest: Array.isArray(sectors)
                    ? sectors.join(", ")
                    : "",
                  consentToShareProfile: eventData?.consentToShare ? 1 : 0,
                  eventId: eventId ? parseInt(eventId, 10) : 0,
                  userId: objectId || "",
                });
                setShowSuccessModal(true);
                setLoading(false);
                submitLockRef.current = false;
                return;
              }

              const returnUrl = `${window.location.origin}${window.location.pathname}`;
              const { paymentIntent, error: confirmError } =
                await stripe.confirmCardPayment(clientSecret, {
                  payment_method: selectedPaymentMethodId,
                  return_url: returnUrl,
                });

              if (confirmError) {
                const confirmMsg =
                  confirmError.message || "Payment confirmation failed.";
                logPaymentError({
                  flow: "event-booking",
                  errorSource: "stripe",
                  message: confirmMsg,
                  error: confirmError,
                });
                // Payment already succeeded (e.g. double submit or back-button) - treat as success
                if (confirmError.code === "payment_intent_unexpected_state") {
                  const sectors = eventData?.sectorsOfInterest;
                  bookEvent({
                    stageOfBusiness: eventData?.stageOfBusiness ?? "",
                    eventObjectives: eventData?.objectives ?? "",
                    sectorsOfInterest: Array.isArray(sectors)
                      ? sectors.join(", ")
                      : "",
                    consentToShareProfile: eventData?.consentToShare ? 1 : 0,
                    eventId: eventId ? parseInt(eventId, 10) : 0,
                    userId: objectId || "",
                  });
                  setShowSuccessModal(true);
                } else {
  const isAuthFailure =
    confirmError.code === "payment_intent_authentication_failure";

  const msg = isAuthFailure
    ? "Authentication was cancelled or failed. Please try again or use another card."
    : confirmError.message || "Payment confirmation failed.";

  setError(msg);
}

setLoading(false);
submitLockRef.current = false;
return;
              }
              if (paymentIntent && paymentIntent.status === "succeeded") {
                const sectors = eventData?.sectorsOfInterest;
                bookEvent({
                  stageOfBusiness: eventData?.stageOfBusiness ?? "",
                  eventObjectives: eventData?.objectives ?? "",
                  sectorsOfInterest: Array.isArray(sectors)
                    ? sectors.join(", ")
                    : "",
                  consentToShareProfile: eventData?.consentToShare ? 1 : 0,
                  eventId: eventId ? parseInt(eventId, 10) : 0,
                  userId: objectId || "",
                });
                setShowSuccessModal(true);
              }
              setLoading(false);
              submitLockRef.current = false;
            },
            onError: (err: unknown) => {
              const source = getPaymentErrorSource(err);
              const msg = getPaymentErrorMessage(err);
              const responseStatus =
                err &&
                typeof err === "object" &&
                "response" in err
                  ? (err as { response?: { status?: number } }).response?.status
                  : undefined;
              const responseData =
                err &&
                typeof err === "object" &&
                "response" in err
                  ? (err as { response?: { data?: unknown } }).response?.data
                  : undefined;
              logPaymentError({
                flow: "event-booking",
                errorSource: source,
                message: msg,
                error: err,
                responseStatus,
                responseData,
              });
              setError(msg);
              setLoading(false);
              submitLockRef.current = false;
            },
          },
        );
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : "Unexpected error occurred.";
        logPaymentError({
          flow: "event-booking",
          errorSource: "frontend",
          message: msg,
          error: err,
        });
        setError(msg);
        setLoading(false);
        submitLockRef.current = false;
      }
    },
    [
      stripe,
      elements,
      useSavedMethod,
      paymentMethods,
      mutate,
      objectId,
      user,
      data,
      eventData,
      eventId,
      bookEvent,
      isEarlyBird,
    ],
  );

  return (
    <div className="m-4 rounded-4 p-4 shadow border-0 d-flex flex-column">
      <form onSubmit={handleSubmit} className="w-100" autoComplete="off">
        <div className="d-flex flex-column gap-3">
          <div className="d-flex justify-content-between">
            <h5>Checkout</h5>
            <h5>Event Booking</h5>
          </div>

          <PriceSummary
            data={data}
            isEarlyBird={isEarlyBird}
            finalPrice={finalPrice}
            membershipName={user?.user?.membershipName}
          />

          <hr />

          <PaymentMethodChoice
            paymentMethods={paymentMethods}
            useSavedMethod={useSavedMethod}
            onUseSaved={handleUseSavedMethod}
            onUseNew={handleUseNewMethod}
          />

          {!useSavedMethod && (
            <div className="row g-2">
              <div className="col-md-6 mb-3">
                <label>Card Number</label>
                <div className="form-control">
                  <CardNumberElement options={stripeElementOptions} />
                </div>
              </div>
              <div className="col-md-3 mb-3">
                <label>Expiry</label>
                <div className="form-control">
                  <CardExpiryElement options={stripeElementOptions} />
                </div>
              </div>
              <div className="col-md-3 mb-3">
                <label>CVC</label>
                <div className="form-control">
                  <CardCvcElement options={stripeElementOptions} />
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label>Billing Address (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Billing Address"
                  value={billingAddress}
                  autoComplete="new-address"
                  onChange={(e) => setBillingAddress(e.target.value)}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger py-2 px-3" role="alert">
              {error}
            </div>
          )}

          <div className="d-flex justify-content-between mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleBack}
            >
              Back
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!stripe || loading}
            >
              {loading ? "Processing..." : "Pay Now"}
            </button>
          </div>
        </div>
      </form>
      {showSuccessModal && (
        <Suspense fallback={null}>
          <EventSuccessModal
            show={showSuccessModal}
            onHide={handleHideSuccess}
            text="Your payment was successful and your booking is confirmed!"
          />
        </Suspense>
      )}
    </div>
  );
}

ConfirmBooking.displayName = "ConfirmBooking";

export default memo(ConfirmBooking);
